import { localDate, PLATFORM_TIMEZONE, type SubscriptionChange } from "@cafe/shared";
import { Alert, Button, Field, FormGrid, formatMoney, Input, Modal, SegmentedControl, Skeleton, toast } from "@cafe/ui";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { http } from "@/shared/api/client";
import { formatDateTime } from "@/shared/lib/options";
import i18n from "@/shared/i18n";
import { handleMutationError, QueryError } from "@/shared/ui/query-error";

type Preview = { total: number; subtotal: number; discount: number; currency: string; trial: boolean; effectiveAt: string; lines: { description: string; amount: number }[]; scheduled: { plan?: string; period?: string }; warnings: { code: string; message: string; details?: unknown }[]; withSubscription: { lastDay: string } };
type Invoice = { id: string; number: string };

/** Последний день срока в N месяцев с даты `from` (YYYY-MM-DD). */
export const termEnd = (from: string, months: number) => {
  const d = new Date(`${from}T00:00:00Z`);
  d.setUTCMonth(d.getUTCMonth() + months);
  d.setUTCDate(d.getUTCDate() - 1);
  return d.toISOString().slice(0, 10);
};
export const nextDay = (date: string) => new Date(Date.parse(`${date}T00:00:00Z`) + 86_400_000).toISOString().slice(0, 10);
export const termDay = (d: string) => new Intl.DateTimeFormat(i18n.language, { day: "2-digit", month: "2-digit", year: "numeric", timeZone: "UTC" }).format(new Date(`${d}T00:00:00Z`));

/**
 * Предпросмотр и оформление изменения (US-PA-08): строки, итог, отложенные изменения, предупреждения о лимитах.
 * Одно дополнение — выбор срока: вместе с подпиской (до конца оплаченного срока) или свой срок «с — по».
 */
export function ChangeModal({ change, title, onClose, term: initial }: { change: SubscriptionChange; title: string; onClose: () => void; term?: { from: string; to?: string } }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const single = !change.planCode && !change.period && change.addons.length === 1 && change.addons[0]!.action === "enable" ? change.addons[0]! : null;
  const today = localDate(PLATFORM_TIMEZONE);
  const [mode, setMode] = useState<"subscription" | "custom">(initial ? "custom" : "subscription");
  const [from, setFrom] = useState(initial?.from ?? today);
  const [to, setTo] = useState(initial?.to ?? termEnd(initial?.from ?? today, 1));
  const custom = !!single && mode === "custom";
  const termValid = !custom || (from >= today && to >= from);
  const req: SubscriptionChange = custom ? { ...change, addons: [{ ...single!, from, to }] } : change;
  const pv = useQuery({ queryKey: ["subscription", "preview", req], queryFn: () => http.post<Preview>("/subscription/preview-change", req), enabled: termValid });
  const [busy, setBusy] = useState(false);
  const p = pv.data;
  const blocked = p?.warnings.some((w) => w.code === "DOWNGRADE_BLOCKED");
  const confirm = async () => {
    setBusy(true);
    try {
      const r = await http.post<Preview & { invoice: Invoice | null }>("/subscription/checkout", req);
      toast.success(r.invoice ? t("billing.invoiceIssued", { n: r.invoice.number }) : t("billing.changeApplied"));
      void qc.invalidateQueries({ queryKey: ["subscription"] });
      void qc.invalidateQueries({ queryKey: ["me"] });
      onClose();
    } catch (e) {
      await handleMutationError(e);
    } finally {
      setBusy(false);
    }
  };
  return (
    <Modal open onOpenChange={(o) => !o && !busy && onClose()} title={title}
      footer={<><Button onClick={() => void confirm()} loading={busy} disabled={!p || blocked || !termValid || pv.isFetching}>{p?.total ? t("billing.issueInvoice", { amount: formatMoney(p.total, p.currency) }) : t("billing.apply")}</Button><Button variant="outline" onClick={onClose}>{t("common.cancel")}</Button></>}>
      {single && (
        <div className="mb-4 flex flex-col gap-3">
          <SegmentedControl fullWidth aria-label={t("billing.term.label")} value={mode} onValueChange={setMode}
            options={[{ value: "subscription", label: t("billing.term.subscription") }, { value: "custom", label: t("billing.term.custom") }]} />
          {custom ? (
            <>
              <FormGrid cols={2}>
                <Field label={t("billing.term.from")} error={from < today ? t("billing.term.past") : undefined}><Input type="date" value={from} min={today} onChange={(e) => e.target.value && setFrom(e.target.value)} /></Field>
                <Field label={t("billing.term.to")} error={to < from ? t("billing.term.order") : undefined}><Input type="date" value={to} min={from} onChange={(e) => e.target.value && setTo(e.target.value)} /></Field>
              </FormGrid>
              <div className="flex flex-wrap gap-2">
                {[1, 3, 6, 12].map((m) => <Button key={m} size="sm" variant={to === termEnd(from, m) ? "secondary" : "outline"} onClick={() => setTo(termEnd(from, m))}>{t("billing.term.months", { count: m })}</Button>)}
              </div>
              <p className="text-sm text-muted">{t("billing.term.customHint")}</p>
            </>
          ) : p && <p className="text-sm text-muted">{t(p.trial ? "billing.term.subscriptionTrialHint" : "billing.term.subscriptionHint", { date: termDay(p.withSubscription.lastDay) })}</p>}
        </div>
      )}
      {pv.isError && <QueryError error={pv.error} onRetry={() => pv.refetch()} />}
      {!termValid ? null : !p ? <Skeleton className="h-32" /> : (
        <div className="flex flex-col gap-4 text-sm">
          {p.trial && p.total === 0 && <Alert tone="info">{t("billing.trialFree")}</Alert>}
          {p.warnings.map((w) => (w.code === "ADDONS_DROPPED"
            ? <Alert key={w.code} tone="warning">{t("billing.addonsDropped", { names: (w.details as { name: string }[]).map((x) => x.name).join(", ") })}</Alert>
            : <Alert key={w.code} tone="danger">{t("billing.downgradeBlocked")}</Alert>))}
          {(p.scheduled.plan || p.scheduled.period) && <Alert tone="info">{t("billing.scheduled", { date: formatDateTime(p.effectiveAt) })}</Alert>}
          {p.lines.length > 0 && (
            <ul className="flex flex-col divide-y divide-border">
              {p.lines.map((l, i) => <li key={i} className="flex justify-between gap-3 py-2"><span>{l.description}</span><span className="tabular-nums">{formatMoney(l.amount, p.currency)}</span></li>)}
            </ul>
          )}
          {p.discount > 0 && <div className="flex justify-between text-muted"><span>{t("billing.discount")}</span><span className="tabular-nums">−{formatMoney(p.discount, p.currency)}</span></div>}
          <div className="flex justify-between border-t border-dashed border-border pt-3 text-base font-semibold"><span>{t("billing.toPay")}</span><span className="tabular-nums">{formatMoney(p.total, p.currency)}</span></div>
          {p.total > 0 && <p className="text-muted">{t("billing.afterPayment")}</p>}
        </div>
      )}
    </Modal>
  );
}

